import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import { Markdown } from '@usalco/catalyst-component-kit';
import { buildArtifactSrcDoc } from '../reporting/v4/htmlJsSandbox';

// Renders a Catalyst mockup artifact inside the embedded chat. Executable kinds (html/svg) carry
// UNTRUSTED author code, so they run in a sandboxed, opaque-origin iframe with the SAME locked-down
// CSP as the promoted htmljs component (reservoir/src/reporting/v4/HtmlJs4.jsx): no network, no
// `allow-same-origin`, inline JS/CSS only, images limited to data:/blob:. `ReservoirKit` is injected
// (via buildArtifactSrcDoc) so a mockup uses the exact helpers the live component will. There is NO
// window.Reservoir bootstrap — a mockup renders baked-in static data, and that absence is what the
// author's promote-time swap (static call → Reservoir.onData(render)) keys off.
//
// markdown renders via the shared Markdown renderer; code (and, for now, mermaid) is SHOWN, never
// executed. See ai/requests/freeform_report/.
export default function ArtifactFrame({ kind, content, language, height = 480 }) {
	const safeContent = typeof content === 'string' ? content : '';
	const isExecutable = kind === 'html' || kind === 'svg';
	const srcDoc = useMemo(
		() => (isExecutable ? buildArtifactSrcDoc(safeContent) : ''),
		[isExecutable, safeContent],
	);

	if (kind === 'markdown') {
		return (
			<Box className="ai-chat-markdown" sx={{ p: 1.5, fontSize: 14, '& p': { mt: 0 } }}>
				<Markdown text={safeContent} />
			</Box>
		);
	}

	if (!isExecutable) {
		// `code` is shown, never executed. `mermaid` rendering is deferred — show its source for now.
		return (
			<Box
				component="pre"
				sx={{ m: 0, p: 1.5, overflow: 'auto', maxHeight: height, fontSize: 12.5, fontFamily: 'monospace', bgcolor: '#F7F8FD', borderRadius: 1 }}
			>
				<code data-language={language || undefined}>{safeContent}</code>
			</Box>
		);
	}

	return (
		<Box
			component="iframe"
			title="Artifact preview"
			srcDoc={srcDoc}
			// Opaque origin: NEVER add allow-same-origin — with allow-scripts that would let the frame
			// remove its own sandbox and reach the app's DOM/cookies/token.
			sandbox="allow-scripts"
			referrerPolicy="no-referrer"
			sx={{ width: '100%', height, border: 'none', bgcolor: '#fff', borderRadius: 1 }}
		/>
	);
}
